import React, { useEffect, useState } from 'react'
import { IconInfoCircle, IconAlertTriangle } from '@tabler/icons-react'
import classNames from 'classnames'
import { CHECKOUT_CART_MESSAGE } from 'shared-types'
import { Button } from '~/components/Button'
import { Link } from '~/components/Link'
import { ProductImage } from '~/components/ProductImage'
import Status from '~/components/Status/Status'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { getImageSrc } from '~/helpers'
import { useDebounce, useFormatPrice, usePage } from '~/hooks'
import { useUserPreferredStore } from '~/hooks/useUserPreferredStore'
import QuantitySelector from '../QuantitySelector'
import styles from './CartItemCard.module.scss'
import { CartItemCardProps } from './CartItemCard.types'

const DEBOUNCE_DELAY = 1000

export const CartItemCard = ({
  item,
  isEditable,
  canRemove = true,
  notice,
  isLoading,
  isOrderedProduct = false,
  linkTarget,
  storeName = '',
  onChangeQuantity,
  removeCartItem,
  isSelectedDelivery,
  isTrafficLight = false,
}: CartItemCardProps) => {
  const { formatPrice } = useFormatPrice()
  const { name, price, discountedPrice, quantity, variant, productSlug } = item
  const { attributesRaw } = variant || {}
  const [isClicked, setIsClicked] = useState(false)
  const [itemQuantity, setItemQuanity] = useState(quantity)
  const debouncedQuantity = useDebounce(itemQuantity, DEBOUNCE_DELAY)
  const { promptUserToSelectStore } = useUserPreferredStore()
  const page = usePage()

  const showTrafficLight =
    notice?.message !== CHECKOUT_CART_MESSAGE.MARKETPLACE_ITEM && isTrafficLight

  useEffect(() => {
    if (!isEditable || !isClicked) {
      return
    }

    if (debouncedQuantity === 0 && removeCartItem) {
      removeCartItem()
    } else {
      onChangeQuantity?.(debouncedQuantity)
    }

    setIsClicked(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuantity, isEditable])

  const getNoticeCssStyle = () => {
    if (item?.isMarketplaceProduct) {
      return {
        '--cart-item-color-warning-text': '#c5006c',
        borderColor: '#c5006c',
      } as React.CSSProperties
    }
    if (!notice?.color) {
      return null
    }
    return {
      '--cart-item-color-warning-text': notice.color,
      borderColor: notice.color,
    } as React.CSSProperties
  }

  const getNoticeCssClass = () => {
    if (notice?.color || notice.variant === 'warning') {
      return 'text-[var(--cart-item-color-warning-text)]'
    }

    return 'text-state-error'
  }

  const getNoticeIconColor = () => {
    if (notice?.color || notice.variant === 'warning') {
      return 'var(--cart-item-color-warning-text)'
    }

    return 'var(--color-state-error)'
  }

  const renderDeliveryStatus = () => {
    let canDeliver = true
    const deliveryErrorMessages: CHECKOUT_CART_MESSAGE[] = [
      CHECKOUT_CART_MESSAGE.ITEM_AVAILABLE_CNC,
      CHECKOUT_CART_MESSAGE.UNAVAILABLE_FOR_DELIVERY,
      CHECKOUT_CART_MESSAGE.DISCONTINUED_PRODUCT,
      CHECKOUT_CART_MESSAGE.REQUESTED_QUANTITY_UNAVAILABLE,
      CHECKOUT_CART_MESSAGE.ORDER_QUANTITY_EXCEED,
      CHECKOUT_CART_MESSAGE.OUT_OF_STOCK,
      CHECKOUT_CART_MESSAGE.NO_DELIVERY_OPTIONS_AVAILABLE,
      CHECKOUT_CART_MESSAGE.SOLD_BY_METER_ITEM,
    ]
    if (
      deliveryErrorMessages.includes(notice?.message as CHECKOUT_CART_MESSAGE)
    ) {
      canDeliver = false
    }
    return (
      <div className='mt-2'>
        <Status
          status={canDeliver ? 'success' : 'error'}
          label={
            <Typography
              tag={TypographyTag.div}
              variant={TypographyVariant.BodyRegular}
              className='py-1 text-secondary-muted'>
              {`${canDeliver ? 'Available' : 'Unavailable'} for ${
                !isSelectedDelivery ? 'collection' : 'delivery'
              }`}
            </Typography>
          }
        />
      </div>
    )
  }

  const openStoreDrawer = () => {
    promptUserToSelectStore?.(variant.sku)
  }

  const renderMarketPlaceStatus = () => {
    return (
      <div className='mt-2'>
        <Status
          status='market'
          label={
            <Typography
              tag={TypographyTag.div}
              variant={TypographyVariant.BodyRegular}
              className='py-1 text-secondary-muted'>
              Direct from Supplier
            </Typography>
          }
        />
      </div>
    )
  }

  const renderStockStatus = () => {
    let hasStock = item?.inStock && item?.inStore
    const stockErrorMessages: CHECKOUT_CART_MESSAGE[] = [
      CHECKOUT_CART_MESSAGE.OUT_OF_STOCK,
      CHECKOUT_CART_MESSAGE.REQUESTED_QUANTITY_UNAVAILABLE,
      CHECKOUT_CART_MESSAGE.ORDER_QUANTITY_EXCEED,
      CHECKOUT_CART_MESSAGE.UNAVAILABLE_FOR_DELIVERY,
      CHECKOUT_CART_MESSAGE.DISCONTINUED_PRODUCT,
    ]
    if (stockErrorMessages.includes(notice?.message as CHECKOUT_CART_MESSAGE)) {
      hasStock = false
    }
    return (
      <div>
        <Status
          status={hasStock ? 'success' : 'error'}
          label={
            <Typography
              tag={TypographyTag.div}
              variant={TypographyVariant.BodyRegular}
              className='py-1 text-secondary-muted'>
              {hasStock ? `In Stock in ` : `Out of stock at `}
              <Button
                variant='link'
                className='body-regular-bold font-bold underline'
                onClick={openStoreDrawer}>
                {storeName}
              </Button>
            </Typography>
          }
        />
      </div>
    )
  }

  const renderSimpleNoticeMessage = () => {
    if (!notice?.message || notice?.customContent) {
      return null
    }
    let message
    let btnMessage
    switch (notice?.message) {
      case CHECKOUT_CART_MESSAGE.OUT_OF_STOCK:
      case CHECKOUT_CART_MESSAGE.ORDER_QUANTITY_EXCEED:
      case CHECKOUT_CART_MESSAGE.REQUESTED_QUANTITY_UNAVAILABLE:
        message = `${CHECKOUT_CART_MESSAGE.REQUESTED_QUANTITY_UNAVAILABLE} Please adjust the quantity or `
        btnMessage = (
          <Button
            theme={{ root: styles.btnRemoveError }}
            variant='link'
            disabled={isLoading}
            noHover
            onClick={() => {
              removeCartItem ? removeCartItem() : onChangeQuantity?.(0)
            }}
            className='text-state-error normal-case'>
            remove the item.
          </Button>
        )
        break
      case CHECKOUT_CART_MESSAGE.UNAVAILABLE_FOR_DELIVERY:
        message = `${CHECKOUT_CART_MESSAGE.UNAVAILABLE_FOR_DELIVERY}. `
        btnMessage = (
          <>
            <Button
              theme={{ root: styles.btnRemoveError }}
              variant='link'
              disabled={isLoading}
              noHover
              onClick={() => {
                removeCartItem ? removeCartItem() : onChangeQuantity?.(0)
              }}
              className='text-state-error normal-case'>
              Remove item
            </Button>
            {` to continue with delivery.`}
          </>
        )
        break
      case CHECKOUT_CART_MESSAGE.DANGEROUS_ITEMS:
        message = `A dangerous goods freight charge applies to this item and will be applied at checkout.`
        break
      case CHECKOUT_CART_MESSAGE.BULKY_GOODS_FREIGHT:
        message = `A bulky goods freight charge applies to this item and will be applied at checkout.`
        break
      case `${CHECKOUT_CART_MESSAGE.MARKETPLACE_ITEM}`:
        if (isSelectedDelivery) {
          message =
            'This Product is Direct from Supplier to store for Click & Collect Only - '
          btnMessage = (
            <>
              <Button
                theme={{ root: styles.btnRemoveError }}
                variant='link'
                disabled={isLoading}
                noHover
                onClick={() => {
                  removeCartItem ? removeCartItem() : onChangeQuantity?.(0)
                }}
                style={{ color: '#c5006c' }}
                className='normal-case'>
                Remove item
              </Button>
              {` to continue with delivery.`}
            </>
          )
        } else {
          message = `${notice?.message}.`
        }
        break
      default:
        message = `${notice?.message}.`
        break
    }

    return (
      <Typography
        tag={TypographyTag.p}
        variant={TypographyVariant.BodySmallBold}
        style={{ fontSize: '14px' }}>
        {message}
        {btnMessage}
      </Typography>
    )
  }

  const renderCustomNoticeMessage = () => {
    if (!notice.customContent) {
      return null
    }

    return (
      <Typography
        tag={TypographyTag.p}
        variant={TypographyVariant.BodySmallBold}>
        {notice.customContent}
      </Typography>
    )
  }

  const isFree = discountedPrice?.centAmount === 0
  return (
    <div className='flex items-start gap-4'>
      <Link
        href={productSlug}
        target={linkTarget}
        className='block relative w-16 h-16 max-w-16 max-h-16 min-h-16 min-w-16 aspect-square rounded-md bg-background-thumb'>
        <ProductImage
          src={getImageSrc(variant?.images?.[0]?.url, '64')}
          addSrcSet={false}
          alt={name}
          width={variant?.images?.[0]?.dimensions?.width}
          height={variant?.images?.[0]?.dimensions?.height}
          className='h-full w-full'
          style={{ objectFit: 'contain' }}
        />
      </Link>
      <div className='flex-1'>
        <div
          className={classNames('flex', {
            'items-start pb-2 gap-2': !isOrderedProduct,
            'flex-col gap-1 pb-6': isOrderedProduct,
          })}>
          <div
            className={classNames('flex w-full gap-2', {
              'flex-col md:flex-row gap-1': isOrderedProduct,
            })}>
            <Link
              href={productSlug}
              target={linkTarget}
              className='block flex-1'>
              <Typography
                tag={TypographyTag.p}
                variant={TypographyVariant.BodyRegularBold}
                className='text-secondary-muted'>
                {name}
              </Typography>
            </Link>

            <div className='flex flex-col gap-2'>
              {discountedPrice && price?.value && (
                <Typography
                  tag={TypographyTag.p}
                  variant={TypographyVariant.BodyRegularBold}
                  className='text-primary-muted !line-through'>
                  {formatPrice(price?.value)}
                </Typography>
              )}
              <Typography
                tag={TypographyTag.p}
                variant={TypographyVariant.BodyRegularBold}
                className='text-secondary-muted mr-8'>
                {isFree ? 'Free' : formatPrice(discountedPrice || price?.value)}
              </Typography>
            </div>
          </div>
          {isOrderedProduct && (
            <>
              <Typography
                tag={TypographyTag.p}
                variant={TypographyVariant.BodySmall}
                className='text-primary-muted'>
                {attributesRaw?.[0]?.name}:
                <span className='inline-block ml-1 font-extrabold'>
                  {attributesRaw?.[0]?.value?.toString()}
                </span>
              </Typography>
              <Typography
                tag={TypographyTag.p}
                variant={TypographyVariant.BodySmall}
                className='text-primary-muted'>
                Quantity
                <span className='font-extrabold inline-block ml-1'>
                  {quantity}
                </span>
              </Typography>
            </>
          )}
        </div>
        {!isOrderedProduct && item?.variant?.sku && (
          <Typography
            variant={TypographyVariant.BodyRegular}
            tag={TypographyTag.p}
            className='text-primary-muted'>
            <span className='whitespace-nowrap font-bold'>
              CAT.NO: {item.variant.sku}
            </span>
          </Typography>
        )}
        {isEditable && (
          <div className='flex gap-2 items-center mt-2'>
            <div className={styles.qty}>
              <QuantitySelector
                minValue={1}
                maxValue={Infinity}
                defaultValue={quantity}
                step={1}
                isLoading={false}
                onChange={(qty) => {
                  setIsClicked(true)
                  setItemQuanity(qty)
                }}
                disabled={isFree}
              />
            </div>

            {canRemove && (
              <Button
                theme={{ root: styles.btnRemove }}
                variant='link'
                noHover
                onClick={() => {
                  removeCartItem ? removeCartItem() : onChangeQuantity?.(0)
                }}
                disabled={isLoading}>
                Remove
              </Button>
            )}
          </div>
        )}

        {showTrafficLight && renderDeliveryStatus()}
        {showTrafficLight && renderStockStatus()}
        {item?.isMarketplaceProduct && renderMarketPlaceStatus()}
        {!isOrderedProduct && notice && (
          <div
            className={classNames(
              'mt-2 border border-red-500 p-2 rounded flex items-center justify-center text-center'
            )}
            style={getNoticeCssStyle()}>
            <IconInfoCircle
              color={getNoticeIconColor()}
              size={16}
              className='mr-1'
            />
            <div className={classNames(getNoticeCssClass())}>
              {renderSimpleNoticeMessage()}
              {renderCustomNoticeMessage()}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
